import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { HalfCircularProgressBar } from '@sorare/core/src/atoms/loader/CircularProgressIndicator/HalfCircularProgressBar/index.tsx';
import { LabelS } from '@sorare/core/src/atoms/typography/index.tsx';
import { Badge } from '@sorare/core/src/atoms/ui/Badge/index.tsx';
import { Chip } from '@sorare/core/src/atoms/ui/Chip/index.tsx';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { ConnectTaskDialog } from '../ConnectTaskDialog/index.tsx';
import { IconWrapper, MissionButton } from '../ui.tsx';
import type {
  ConnectTaskWidget_cardSetRoot,
  ConnectTaskWidget_currentUser,
} from './__generated__/index.graphql.ts';
import request from './assets/connectIcon.webp';

const StyledChip = styled(Chip)`
  position: absolute;
  top: 84%;
  left: 50%;
  transform: translate(-50%, 0px);
`;

type Props = {
  currentUser: ConnectTaskWidget_currentUser;
  cardSet: ConnectTaskWidget_cardSetRoot;
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
};

export const ConnectTaskWidget = ({
  currentUser,
  cardSet,
  open,
  onClose,
  onOpen,
}: Props) => {
  const { inviteTasksAsReferrer } = currentUser || {};

  const { maximumInviteTasksAsReferrer } = cardSet || {};

  const typedInviteTasks = inviteTasksAsReferrer.filter(
    isTypeFn('InviteTaskAsReferrer')
  );
  return (
    <Badge
      badgeContent={
        typedInviteTasks.filter(task =>
          task.steps.some(
            stepTask =>
              stepTask.aasmState === ManagerProgressionTaskState.COMPLETED
          )
        ).length
      }
      overlap="circular"
    >
      <MissionButton onClick={onOpen}>
        <HalfCircularProgressBar
          size={6}
          progresses={[
            inviteTasksAsReferrer.length / (maximumInviteTasksAsReferrer || 1),
          ]}
          colors={[{ from: 'var(--c-set-surface-primary)' }]}
        />
        <IconWrapper>
          <img src={request} alt="target" width={24} height={24} />
        </IconWrapper>
        <StyledChip>
          <LabelS>
            <b>{inviteTasksAsReferrer.length}</b>/{maximumInviteTasksAsReferrer}
          </LabelS>
        </StyledChip>
      </MissionButton>

      {!!open && <ConnectTaskDialog open={open} onClose={onClose} />}
    </Badge>
  );
};

ConnectTaskWidget.fragments = {
  currentUser: gql`
    fragment ConnectTaskWidget_currentUser on CurrentUser {
      slug
      inviteTasksAsReferrer(sport: $sport) {
        id
        ... on InviteTaskAsReferrer {
          id
          steps {
            id
            aasmState
          }
        }
      }
    }
  ` as TypedDocumentNode<ConnectTaskWidget_currentUser>,
  cardSetRoot: gql`
    fragment ConnectTaskWidget_cardSetRoot on CardSetRoot {
      id
      maximumInviteTasksAsReferrer(sport: $sport)
    }
  ` as TypedDocumentNode<ConnectTaskWidget_cardSetRoot>,
};
